import React, { useEffect, useState } from "react";
import AppModal from "../../components/layout/app/AppModal";
import { useToggleTop } from "../../components/common/ToggleTop";
import { logOut } from "../../components/layout/app/SideMenu";
import { UserService } from "../../services/UserService";
import useAlive from "../../hooks/useAlive";
import "../../assets/styles/app-preferences.css";

// The terms the app shows (app/screens/PrivacyPolicyModal.js), for a policy
// version whose own text (content) the API leaves empty.
const APP_TERMS = [
  { heading: "Terms and Conditions" },
  { title: "1. Introduction", text: "Welcome to HASHh Connect, an application provided by HASHh Automations. By using our app, you agree to these Terms and Conditions. Please read them carefully." },
  { title: "2. Services Provided", text: "HASHh Connect allows users to control and monitor smart home devices manufactured by HASHh Automations. The app may also collect data to enhance user experience and improve our services." },
  { title: "3. User Accounts", text: "To access certain features of HASHh Connect, you may be required to create an account. You are responsible for maintaining the confidentiality of your account information and for all activities that occur under your account." },
  { title: "4. Use of Firebase and Google Analytics", text: "Our app uses Firebase and Google Analytics to collect data on how users interact with the app. This data helps us understand user behavior, improve the app, and enhance user experience." },
  { title: "5. Use of Apple App Store Analytics", text: "HASHh Connect uses Apple App Store Analytics to track app performance and usage on iOS devices. This data helps us improve the app and provide a better user experience." },
  { title: "6. Hardware Data Collection", text: "Our app may collect data from smart home devices, including but not limited to device status, usage patterns, and performance metrics. This data is used to improve the functionality and performance of our products and services." },
  { title: "7. Privacy", text: "Your privacy is important to us. Please review our Privacy Policy for detailed information on how we collect, use, and protect your personal information." },
  { title: "8. Changes to Terms", text: "We reserve the right to modify these Terms and Conditions at any time. Any changes will be effective immediately upon posting on the app. Your continued use of the app following the posting of changes will constitute your acceptance of such changes." },
  { title: "9. Contact Us", text: "If you have any questions or concerns about these Terms and Conditions, please contact us at support@hashh.io." },
  { heading: "Privacy Policy" },
  { title: "1. Introduction", text: 'This Privacy Policy describes how HASHh Automations ("we," "our," "us") collects, uses, and discloses your personal information when you use the HASHh Connect app.' },
  { title: "2. Information We Collect", text: "2.1. Personal Information\nWe may collect personal information that you provide directly to us, such as your name, email address, and account details.\n\n2.2. Usage Data\nWe collect data on how you interact with our app, including but not limited to your device information, IP address, and usage patterns. This data is collected through Firebase, Google Analytics, and Apple App Store Analytics.\n\n2.3. Hardware Data\nWe collect data from smart home devices connected to the HASHh Connect app. This data includes device status, usage patterns, and performance metrics." },
  { title: "3. How We Use Your Information", text: "We use the collected information to:\n- Improve and personalize your experience with the app.\n- Enhance the performance and functionality of our products.\n- Understand user behavior and app usage.\n- Provide customer support and respond to inquiries." },
  { title: "4. Data Sharing and Disclosure", text: "We do not share your personal information with third parties except as described in this Privacy Policy. We may share your information with:\n- Service providers who perform services on our behalf.\n- Law enforcement or regulatory authorities if required by law." },
  { title: "5. Data Security", text: "We take reasonable measures to protect your personal information from unauthorized access, use, or disclosure. However, no security system is impenetrable, and we cannot guarantee the absolute security of your data." },
  { title: "6. Your Rights", text: "You have the right to access, correct, or delete your personal information. You can manage your data and privacy settings within the app or by contacting us at support@hashh.io." },
  { title: "7. Changes to This Privacy Policy", text: "We may update this Privacy Policy from time to time. Any changes will be effective immediately upon posting on the app. Your continued use of the app following the posting of changes will constitute your acceptance of such changes." },
  { title: "8. Contact Us", text: "If you have any questions or concerns about this Privacy Policy, please contact us at support@hashh.io." },
];

// A policy version's own text, as plain text: tags are dropped, never run, and
// paragraphs, headings and list items keep their own lines. The saved text
// (version 1) marks most line breaks with " /n" instead of a newline.
const textOf = (content) => {
  if (typeof content !== "string" || !content.trim()) return null;
  content = content.replace(/ \/n/g, "\n");
  if (!/<[a-z!/]/i.test(content)) return content.trim();
  const doc = new DOMParser().parseFromString(content, "text/html");
  doc.querySelectorAll("script, style").forEach((element) => element.remove());
  doc.querySelectorAll("br").forEach((element) => element.replaceWith("\n"));
  doc.querySelectorAll("p, div, li, tr, h1, h2, h3, h4, h5, h6").forEach((element) => element.append("\n"));
  const text = doc.body.textContent.replace(/\n{3,}/g, "\n\n");
  return text.trim() || null;
};

// Versions agreed to on this page load, so Home doesn't ask again.
const agreedHere = new Set();

// The app's terms and privacy prompt (FirstScreen.js fetchPolicies and
// PrivacyPolicyModal.js): shown on Home when the user hasn't agreed to the
// latest policy version. Agree records it; Disagree signs out, as the app
// closes. The text is the version's own when the API has it, else the app's.
function PrivacyPolicyModal({ homeId }) {
  const toast = useToggleTop();
  const alive = useAlive();
  const [policy, setPolicy] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!homeId) return undefined;
    let current = true;
    Promise.all([UserService.getPolicy(), UserService.getPolicyLatest()])
      .then(([mine, latest]) => {
        const newest = Array.isArray(latest) ? latest[0] : null;
        if (!current || !newest || agreedHere.has(newest.version)) return;
        const agreed = Array.isArray(mine) && mine.length ? Number(mine[0].policy_version) : null;
        if (agreed === null || agreed < Number(newest.version)) setPolicy(newest);
      })
      .catch(() => {
        // no prompt when the policies can't be read; Home asks again next time
      });
    return () => {
      current = false;
    };
  }, [homeId]);

  if (!policy) return null;
  const ownText = textOf(policy.content);

  const agree = async () => {
    setSaving(true);
    try {
      await UserService.createPolicy({ policy_id: policy.id, has_agreed: true, policy_version: policy.version });
      agreedHere.add(policy.version);
      if (alive.current) setPolicy(null);
    } catch (error) {
      toast("Couldn't save your agreement. Try again.", "red");
    } finally {
      if (alive.current) setSaving(false);
    }
  };

  return (
    <AppModal
      open
      onClose={() => {}}
      title="Terms and Conditions & Privacy Policy"
      className="hx-policy"
      actions={
        <>
          <button type="button" className="hx-btn is-ghost" onClick={logOut} disabled={saving}>
            Disagree
          </button>
          <button type="button" className="hx-btn" onClick={agree} disabled={saving}>
            {saving ? "Saving…" : "Agree"}
          </button>
        </>
      }
    >
      <p className="hx-modal-text">
        We've updated our Privacy Policy to meet new legal standards; by clicking Agree, you confirm acceptance of the
        updated terms.
      </p>
      <div className="hx-policy-text" tabIndex={0} aria-label="Terms and Privacy Policy">
        {ownText ? (
          <p>{ownText}</p>
        ) : (
          APP_TERMS.map((part, index) =>
            part.heading ? (
              <h3 key={index}>{part.heading}</h3>
            ) : (
              <section key={index}>
                <h4>{part.title}</h4>
                <p>{part.text}</p>
              </section>
            )
          )
        )}
      </div>
    </AppModal>
  );
}

export default PrivacyPolicyModal;
